import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import styles from './Terminal.module.css';
import { useTypewriter } from '../../hooks/useTypewriter';
import type { TerminalPhase, HistoryItem } from '../../hooks/useTerminal';

type TerminalProps = {
  phase: TerminalPhase;
  history: HistoryItem[];
  input: string;
  setInput: (v: string) => void;
  username: string;
  lineIndex: number;
  bootLines: string[];
  handleKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
};

export function Terminal({
  phase, 
  history, 
  input, 
  setInput, 
  lineIndex, 
  bootLines, 
  handleKeyDown
}: TerminalProps) {
  const terminalRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [showCursor, setShowCursor] = useState(true);

  const currentLine = useTypewriter(bootLines[lineIndex] || '', 30);

  useEffect(() => {
    if (phase === 'command' || phase === 'password') {
      inputRef.current?.focus();
    }
  }, [phase]);

  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [history, phase, currentLine, lineIndex]);

  useEffect(() => {
    const interval = setInterval(() => setShowCursor((p) => !p), 500);
    return () => clearInterval(interval);
  }, []);

  const handleTerminalClick = () => {
    if (phase === 'command' || phase === 'password') {
      inputRef.current?.focus();
    }
  };

  // EXEC 按钮：等价于在输入框按 Enter
  const handleExecClick = () => {
    handleKeyDown({ key: 'Enter', preventDefault: () => {} } as KeyboardEvent<HTMLInputElement>);
    inputRef.current?.focus();
  };

  const renderPrompt = () => {
    if (phase === 'password') return 'PASSWORD>';
    return 'SYS_ADMIN>';
  };

  return (
    <>
      <div 
        className={styles.terminalOutput} 
        ref={terminalRef} 
        onClick={handleTerminalClick}
      >
        {phase === 'boot' && bootLines.slice(0, lineIndex).map((line, i) => (
          <div key={i} className={styles.termLine}>
            <span className={styles.termText}>{line}</span>
          </div>
        ))}
        
        {phase === 'boot' && lineIndex < bootLines.length && (
          <div className={styles.termLine}>
            <span className={styles.termText}>{currentLine}<span className={styles.cursor}>_</span></span>
          </div>
        )}

        {(phase === 'command' || phase === 'password' || history.length > 0) && (
          <>
            {bootLines.map((line, i) => (
               <div key={`boot-${i}`} className={styles.termLine}>
                  <span className={styles.termDim}>{line}</span>
               </div>
            ))}
            {history.map((item, i) => (
              <div key={i} className={styles.termLine}>
                {item.type === 'cmd' && <span className={styles.termPrefix}>{renderPrompt()} </span>}
                <span 
                  className={`${
                    item.type === 'success' ? styles.termSuccess :
                    item.type === 'err' ? styles.termError :
                    item.type === 'cmd' ? styles.termText :
                    item.type === 'dim' ? styles.termDim :
                    styles.termText
                  } ${item.onClick ? styles.termClickable : ''}`}
                  onClick={item.onClick ? (e) => { e.stopPropagation(); item.onClick!(); } : undefined}
                >
                  {item.text}
                </span>
              </div>
            ))}
          </>
        )}
      </div>

      <div className={styles.inputContainer} onClick={handleTerminalClick}>
        <span className={styles.inputPrompt}>{renderPrompt()}</span>
        <div className={styles.inputWrapper}>
          <span className={styles.inputText}>
            {phase === 'password' ? '*'.repeat(input.length) : input}
          </span>
          {showCursor && <span className={styles.cursor}>█</span>}
          <input
            id="cmd-input"
            ref={inputRef}
            type="text"
            className={styles.hiddenInput}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={(e) => {
              if (phase === 'command' || phase === 'password') {
                setTimeout(() => e.target.focus(), 10);
              }
            }}
            autoFocus
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
          />
        </div>
        <button type="button" className={styles.execButton} onClick={handleExecClick}>EXEC</button>
      </div>
    </>
  );
}
